import React, { useEffect, useState } from 'react';
import { GlassCard } from './GlassCard';
import { getWeeklyDigest, getSymptomClusters } from '../lib/api';

const METRIC_META = {
  mood: { label: 'Mood', icon: 'fa-face-smile', color: '#7C3AED', goodHigh: true },
  energy: { label: 'Energy', icon: 'fa-bolt', color: '#F59E0B', goodHigh: true },
  sleep: { label: 'Sleep', icon: 'fa-moon', color: '#6366F1', goodHigh: true },
  stress: { label: 'Stress', icon: 'fa-wind', color: '#14B8A6', goodHigh: false },
};

export function WeeklyDigest({ userId }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    getWeeklyDigest(userId).then(setD).finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <GlassCard className="p-6 text-center text-[var(--aura-ink-soft)]"><i className="fa-solid fa-circle-notch fa-spin" /></GlassCard>;
  if (!d) return null;

  if (!d.has_data) {
    return (
      <GlassCard className="p-6" data-testid="weekly-digest-empty">
        <div className="flex items-center gap-2 mb-1"><i className="fa-solid fa-calendar-week text-[var(--aura-purple)]" /><h3 className="font-display text-lg">This week</h3></div>
        <p className="text-sm text-[var(--aura-ink-soft)]">{d.message}</p>
      </GlassCard>
    );
  }

  return (
    <GlassCard className="p-6" data-testid="weekly-digest" style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.06), rgba(236,72,153,0.05))' }}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="grid place-items-center w-10 h-10 rounded-xl bg-[var(--aura-purple)]/12 text-[var(--aura-purple)]"><i className="fa-solid fa-calendar-week" /></div>
          <div>
            <h3 className="font-display text-lg leading-tight">Your week in review</h3>
            <p className="text-xs text-[var(--aura-ink-soft)]">{d.headline}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        {Object.entries(METRIC_META).map(([k, m]) => {
          const val = d.averages[k];
          const delta = d.deltas?.[k] || 0;
          const up = delta > 0.15;
          const down = delta < -0.15;
          const good = up ? m.goodHigh : m.goodHigh ? false : true;
          return (
            <div key={k} className="rounded-xl bg-white/70 p-3">
              <div className="flex items-center gap-1.5 text-xs text-[var(--aura-ink-soft)] mb-1"><i className={`fa-solid ${m.icon}`} style={{ color: m.color }} /> {m.label}</div>
              <div className="flex items-baseline gap-1">
                <span className="font-display text-xl" style={{ color: m.color }}>{val}</span>
                <span className="text-[10px] text-[var(--aura-ink-soft)]">/5</span>
                {d.has_comparison && (up || down) && (
                  <span className="ml-auto text-[11px] font-semibold" style={{ color: good ? '#10B981' : '#EF4444' }}>
                    <i className={`fa-solid ${up ? 'fa-caret-up' : 'fa-caret-down'}`} /> {Math.abs(delta)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {d.top_symptoms?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="text-xs text-[var(--aura-ink-soft)] self-center">Most logged:</span>
          {d.top_symptoms.map((s) => (
            <span key={s.name} className="text-xs rounded-full px-2.5 py-1 bg-[var(--aura-purple)]/8 text-[var(--aura-purple)]">{s.name} · {s.days}d</span>
          ))}
        </div>
      )}

      <div className="flex items-start gap-2 rounded-xl bg-white/70 p-3">
        <i className="fa-solid fa-seedling mt-0.5" style={{ color: '#14B8A6' }} />
        <p className="text-sm text-[var(--aura-ink)]">{d.suggestion}</p>
      </div>
    </GlassCard>
  );
}

export function SymptomClusters({ userId }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    getSymptomClusters(userId).then(setD).finally(() => setLoading(false));
  }, [userId]);

  if (loading) return null;
  if (!d) return null;

  const factorStyle = (f) => (f === 'poor sleep' ? { color: '#6366F1', icon: 'fa-moon', bg: 'rgba(99,102,241,0.1)' } : { color: '#14B8A6', icon: 'fa-wind', bg: 'rgba(20,184,166,0.1)' });

  return (
    <GlassCard className="p-6" data-testid="symptom-clusters">
      <div className="flex items-center gap-2 mb-1">
        <div className="grid place-items-center w-9 h-9 rounded-xl bg-[var(--aura-purple)]/12 text-[var(--aura-purple)]"><i className="fa-solid fa-diagram-project" /></div>
        <h3 className="font-display text-lg">Symptom pattern insights</h3>
      </div>
      <p className="text-sm text-[var(--aura-ink-soft)] mb-4">{d.summary}</p>

      {d.clusters?.length > 0 && (
        <div className="space-y-2">
          {d.clusters.map((c, i) => {
            const st = factorStyle(c.factor);
            return (
              <div key={i} className="flex items-start gap-3 rounded-xl p-3" style={{ background: st.bg }} data-testid={`cluster-${i}`}>
                <div className="grid place-items-center w-9 h-9 rounded-lg shrink-0" style={{ background: '#fff', color: st.color }}><i className={`fa-solid ${st.icon}`} /></div>
                <div>
                  <div className="text-sm font-semibold">
                    {c.symptom} <span className="font-normal text-[var(--aura-ink-soft)]">links with</span> <span style={{ color: st.color }}>{c.factor}</span>
                  </div>
                  <div className="text-xs text-[var(--aura-ink-soft)]">{c.detail}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </GlassCard>
  );
}
